import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Shows the final price for the quantity chosen on the product page (price per bottle × bottles).
 * It follows the quantity selector and variant changes in the same section.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} amount - The formatted total.
 * @property {HTMLElement} bottles - The number of bottles.
 *
 * @extends Component<Refs>
 */
class MwOrderTotal extends Component {
  requiredRefs = ['amount', 'bottles'];

  /** @type {HTMLElement | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity);
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariant);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity);
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariant);
    this.#section = null;
  }

  /** @param {Event} event */
  #onQuantity = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.cartLine) return;
    const quantity = Number(detail?.quantity);
    if (quantity > 0) this.#render(quantity);
  };

  /** @param {Event} event */
  #onVariant = (event) => {
    const html = /** @type {CustomEvent} */ (event).detail?.data?.html;
    if (!(html instanceof Document || html instanceof DocumentFragment || html instanceof Element)) return;

    const next = html.querySelector(`mw-order-total[data-block-id="${this.dataset.blockId}"]`);
    if (!(next instanceof HTMLElement) || !next.dataset.unitPrice) return;

    this.dataset.unitPrice = next.dataset.unitPrice;
    this.#render(this.#currentQuantity());
  };

  #currentQuantity() {
    const input = this.#section?.querySelector('quantity-selector-component input[name="quantity"]');
    return Number(input instanceof HTMLInputElement ? input.value : this.dataset.quantity) || 1;
  }

  /** @param {number} quantity */
  #render(quantity) {
    const unitPrice = Number(this.dataset.unitPrice) || 0;
    const { moneyFormat = '{{amount}}', currency = 'CHF', bottleOne = '', bottleOther = '' } = this.dataset;

    this.refs.amount.textContent = formatMoney(unitPrice * quantity, moneyFormat, currency);
    this.refs.bottles.textContent = (quantity === 1 ? bottleOne : bottleOther).replace('__N__', String(quantity));
  }
}

if (!customElements.get('mw-order-total')) {
  customElements.define('mw-order-total', MwOrderTotal);
}
